/** Path prefix of the Complete Waivers page, whose URL carries a capability token. */
export const COMPLETE_WAIVERS_PATH_PREFIX = '/complete-waivers/';
/** Path prefix of the participant signing page, whose URL carries a signing token. */
export const PARTICIPANT_WAIVERS_PATH_PREFIX = '/participant-waivers/';
/**
 * Path prefix of the SMS short-link redirect. Its token resolves to the
 * customer's waiver link — the Complete Waivers portal — so it grants the
 * same access as the portal token it stands for.
 */
export const SHORT_LINK_PATH_PREFIX = '/s/';
/** Every public page whose URL carries a bearer token. */
export const TOKEN_PAGE_PATH_PREFIXES = [COMPLETE_WAIVERS_PATH_PREFIX, PARTICIPANT_WAIVERS_PATH_PREFIX, SHORT_LINK_PATH_PREFIX];

/** The slice of `window` the guard reads and writes. */
export type AnalyticsGuardWindow = { location: { pathname: string } } & Record<string, unknown>;

/**
 * Keeps Complete Waivers and participant signing tokens out of Google Analytics.
 *
 * AngularFireAnalyticsModule loads Firebase Analytics eagerly at startup,
 * and its default config sends an automatic `page_view` carrying the full
 * page URL — which on this page includes the token. Setting Google's
 * documented `window['ga-disable-<measurementId>'] = true` before the SDK
 * initializes disables collection for that measurement ID for the rest of
 * the page load.
 *
 * Narrowly scoped: only a page load that STARTS on /complete-waivers/,
 * /participant-waivers/ or an SMS short link /s/ (customers arrive there
 * from a shared link; the portal opens participant pages in-app, from a
 * page load that is already excluded). Every other page load is untouched.
 * Must run before the Angular app bootstraps.
 *
 * @return Whether analytics was disabled for this page load.
 */
export function disableAnalyticsOnCompleteWaivers(
  win: AnalyticsGuardWindow,
  measurementId: string | undefined,
): boolean {
  if (!measurementId) return false;
  const pathname = win?.location?.pathname || '';
  if (!TOKEN_PAGE_PATH_PREFIXES.some(prefix => pathname.startsWith(prefix))) return false;
  win[`ga-disable-${measurementId}`] = true;
  return true;
}

/** Whether an app URL or path (query and fragment allowed) is a token-bearing page. */
export function isTokenPageUrl(url: string): boolean {
  const path = (url || '').split(/[?#]/)[0];
  return TOKEN_PAGE_PATH_PREFIXES.some(prefix => path.startsWith(prefix));
}

/** The router events the navigation guard reads: anything with a `url`, tagged by type. */
export interface TokenPageNavigationEvent {
  url?: string;
}

/**
 * The client-side half of the guard. GA4's enhanced measurement records
 * a `page_view` for history changes, with the new URL and the previous one
 * as referrer — so an in-app navigation INTO a token page from a page with
 * analytics running would report the token. Before the router changes the
 * URL (`NavigationStart`), this sets the same `ga-disable-<id>` flag
 * Firebase's own `setAnalyticsCollectionEnabled(false)` sets, which gtag
 * honours for every later hit.
 *
 * It is never switched back on for the rest of the page load: the first
 * page_view after leaving would carry the token URL as its referrer.
 *
 * @param events Router events (only `NavigationStart`s are passed in).
 * @param win The window whose flag is set.
 * @param measurementId The GA measurement id; nothing happens without one.
 * @return Unsubscribe.
 */
export function disableAnalyticsOnTokenPageNavigation(
  events: { subscribe(next: (event: TokenPageNavigationEvent) => void): { unsubscribe(): void } },
  win: Record<string, unknown>,
  measurementId: string | undefined,
): () => void {
  if (!measurementId) return () => undefined;
  const subscription = events.subscribe(event => {
    if (event && typeof event.url === 'string' && isTokenPageUrl(event.url)) {
      win[`ga-disable-${measurementId}`] = true;
    }
  });
  return () => subscription.unsubscribe();
}
